// src/pages/public/SelectTimePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { db } from "../../config/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  doc,
  getDoc,
} from "firebase/firestore";

//
// UPDATED WORK HOURS
// Salon opens at 10:30 AM (10.5 hours)
// Weekday close: 22 (10 PM)
// Weekend close: 18 (6 PM)
//
const WORK_START = 10.5; // 10:30 AM
const SLOT_MIN = 15;
const DEFAULT_DUR = 60;
const BUFFER_MIN = 0;

/** Hook: phone detection */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );

  useEffect(() => {
    const handler = () => {
      setIsMobile(window.innerWidth <= breakpoint);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);

  return isMobile;
}

export default function SelectTimePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();
  const isMobile = useIsMobile();

  const hero =
    "https://assets.api.uizard.io/api/cdn/stream/597fae2c-5bf0-46f2-ac43-e83205eb5d64.png";

  // read state/query
  const stateStaffId = location.state?.staffId ?? null;
  const stateServices = location.state?.services ?? [];
  const stateTotals = location.state?.totals ?? null;

  const qpTotals = {
    count: Number(sp.get("count") || 0),
    totalPrice: Number(sp.get("total") || 0),
    totalMin: Number(sp.get("dur") || DEFAULT_DUR),
  };

  const staffId = stateStaffId ?? (sp.get("staff") || "");
  const services = stateServices;
  const totals = stateTotals ?? qpTotals;

  const durationMin = Number(totals.totalMin || DEFAULT_DUR);
  const servicesCount = Number(totals.count || 0);
  const totalCost = Number(totals.totalPrice || 0);

  const [dateStr, setDateStr] = useState(() => toYMD(new Date()));
  const [bookings, setBookings] = useState([]);
  const [activeStaff, setActiveStaff] = useState([]);
  const [bookingsByStaff, setBookingsByStaff] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);

  const [assignedStaffId, setAssignedStaffId] = useState(null);
  const [assignedStaffName, setAssignedStaffName] = useState(null);

  const [staffName, setStaffName] = useState(() => {
    if (!staffId) return "";
    return staffId === "any" ? "Any staff" : location.state?.staffName || "";
  });

  // fetch staff name
  useEffect(() => {
    let ignore = false;
    if (!staffId || staffId === "any" || staffName) return;

    const run = async () => {
      try {
        const snap = await getDoc(doc(db, "employees", staffId));
        if (!ignore) {
          const data = snap.exists() ? snap.data() : null;
          setStaffName(data?.name || staffId);
        }
      } catch {
        if (!ignore) setStaffName(staffId);
      }
    };

    run();
    return () => (ignore = true);
  }, [staffId, staffName]);

  // Fetch bookings for selected day
  useEffect(() => {
    if (!staffId) return;
    let cancelled = false;

    const fetchForDay = async () => {
      setLoading(true);
      try {
        const { start: dayStart } = dayBounds(dateStr);
        const { workStart, workEnd } = getWorkHoursForDate(dayStart);

        if (staffId !== "any") {
          const q1 = query(
            collection(db, "bookings"),
            where("staffId", "==", staffId),
            where("date", "==", dateStr)
          );
          const snap = await getDocs(q1);
          if (cancelled) return;

          const rowsRaw = snap.docs.map((d) => {
            const data = d.data() || {};
            return {
              id: d.id,
              start: normalizeTime(data.start),
              end: normalizeTime(data.end),
              staffId: data.staffId,
              ...data,
            };
          });

          const rows = rowsRaw
            .filter((b) => b.start && b.end && !isNaN(+b.start) && !isNaN(+b.end))
            .sort((a, b) => +a.start - +b.start);

          setBookings(rows);
          setActiveStaff([]);
          setBookingsByStaff({});
        } else {
          // ANY STAFF
          const empSnap = await getDocs(
            query(collection(db, "employees"), where("active", "==", true))
          );
          const staffList = empSnap.docs.map((d) => ({
            id: d.id,
            name: d.data()?.name || d.id,
          }));

          const ids = staffList.map((s) => s.id);
          const batches = chunk(ids, 10);
          const allRowsRaw = [];

          for (const batch of batches) {
            const q2 = query(
              collection(db, "bookings"),
              where("staffId", "in", batch),
              where("date", "==", dateStr)
            );
            const snap = await getDocs(q2);
            snap.docs.forEach((d) => {
              const data = d.data() || {};
              allRowsRaw.push({
                id: d.id,
                start: normalizeTime(data.start),
                end: normalizeTime(data.end),
                staffId: data.staffId,
                ...data,
              });
            });
          }

          const allRows = allRowsRaw
            .filter((b) => b.start && b.end && !isNaN(+b.start) && !isNaN(+b.end))
            .sort((a, b) => +a.start - +b.start);

          const map = {};
          for (const s of staffList) map[s.id] = [];
          for (const r of allRows) {
            if (!map[r.staffId]) map[r.staffId] = [];
            map[r.staffId].push(r);
          }

          setActiveStaff(staffList);
          setBookingsByStaff(map);
          setBookings([]);
        }
      } catch {
        setBookings([]);
        setActiveStaff([]);
        setBookingsByStaff({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchForDay();
    return () => (cancelled = true);
  }, [staffId, dateStr]);

  //
  // BUILD AVAILABLE SLOTS
  //
  const available = useMemo(() => {
    const { start: dayStart } = dayBounds(dateStr);
    const { workStart, workEnd } = getWorkHoursForDate(dayStart);

    let slots = makeSlotsForDay(dayStart, workStart, workEnd, SLOT_MIN);

    //
    // NEW FEATURE: remove past slots if booking for today
    //
    const now = new Date();
    const isToday = dateStr === toYMD(now);
    if (isToday) {
      slots = slots.filter((s) => s > now);
    }

    // SPECIFIC STAFF
    if (staffId !== "any") {
      return slots
        .map((slot) => ({
          slot,
          free: canFitAndIsFree(
            slot,
            durationMin,
            bookings,
            workEnd,
            BUFFER_MIN
          ),
        }))
        .filter((x) => x.free)
        .map((x) => ({ slot: x.slot }));
    }

    // ANY STAFF
    return slots
      .map((slot) => {
        const freeStaff = firstFreeStaff(
          slot,
          durationMin,
          bookingsByStaff,
          workEnd,
          BUFFER_MIN
        );
        return freeStaff ? { slot, staff: freeStaff } : null;
      })
      .filter(Boolean);
  }, [dateStr, bookings, bookingsByStaff, staffId, durationMin]);

  const summary = useMemo(
    () => ({
      total: isFinite(totalCost) ? `C$ ${totalCost}` : "C$ 0",
      services: servicesCount,
      duration: formatMinutes(durationMin),
    }),
    [servicesCount, durationMin, totalCost]
  );

  //
  // UI
  //
  const heroBoxStyle = isMobile ? sx.heroBoxMobile : sx.heroBoxDesktop;
  const containerStyle = isMobile ? sx.containerMobile : sx.containerDesktop;
  const timeWrapStyle = {
    ...sx.timeWrap,
    ...(isMobile ? sx.timeWrapMobile : {}),
  };
  const bottomBarStyle = isMobile ? sx.bottomBarMobile : sx.bottomBarDesktop;

  return (
    <div style={sx.screen}>
      <div style={sx.header} />

      {/* Hero */}
      <div style={heroBoxStyle}>
        <div style={{ ...sx.heroImage, backgroundImage: `url(${hero})` }} />
        <div
          style={{
            ...sx.heroOverlay,
            ...(isMobile ? sx.heroOverlayMobile : {}),
          }}
        >
          <div style={{ display: "grid", gap: 6 }}>
            <div style={sx.heroTitle}>613Nail Hub</div>
            <div style={sx.heroMeta}>
              📍 598 Rideau St, Ottawa, ON K1N 6C6 <br /> ☎︎ (613) 327-4049
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={containerStyle}>
        <div style={sx.centerRow}>
          <div style={sx.calendarBox}>
            <div style={sx.sectionTitle}>Select date</div>

            <input
              type="date"
              value={dateStr}
              onChange={(e) => {
                setSelectedTime(null);
                setAssignedStaffId(null);
                setAssignedStaffName(null);
                setDateStr(e.target.value);
              }}
              min={toYMD(new Date())}
              style={sx.dateInput}
            />

            {/* Time selection */}
            <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
              <div style={timeWrapStyle}>
                <div style={sx.timeHeader}>
                  <span style={sx.timeHeaderText}>
                    {`Availability — ${
                      staffId === "any" ? "Any staff" : staffName || "…"
                    }`}
                  </span>
                </div>

                <div
                  style={{
                    padding: "10px 12px",
                    color: "#6b7280",
                    fontWeight: 700,
                  }}
                >
                  {loading ? "Checking availability…" : "Available times"}
                </div>

                {!loading && available.length === 0 && (
                  <div
                    style={{
                      padding: 16,
                      color: "#6b7280",
                      fontWeight: 600,
                    }}
                  >
                    No time available for this day.
                  </div>
                )}

                {!loading && available.length > 0 && (
                  <div style={sx.timeGrid}>
                    {available.map(({ slot, staff: freeStaff }) => {
                      const label = fmtTime(slot);
                      const active =
                        selectedTime && +selectedTime === +slot;

                      return (
                        <button
                          key={+slot}
                          onClick={() => {
                            setSelectedTime(slot);
                            if (staffId === "any" && freeStaff) {
                              setAssignedStaffId(freeStaff.id);
                              setAssignedStaffName(freeStaff.name);
                            } else {
                              setAssignedStaffId(null);
                              setAssignedStaffName(null);
                            }
                          }}
                          style={{
                            ...sx.timeBtn,
                            ...(active ? sx.timeBtnActive : {}),
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Bottom summary */}
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={bottomBarStyle}>
                <div style={sx.summary}>
                  <div style={sx.dot} /> <span>{summary.total}</span>
                  <div style={{ width: 12 }} />
                  <div style={sx.dot} />{" "}
                  <span>
                    {summary.services}{" "}
                    {summary.services === 1 ? "service" : "services"} –{" "}
                    {summary.duration}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    ...(isMobile
                      ? { width: "100%", flexDirection: "column-reverse" }
                      : {}),
                  }}
                >
                  <button
                    style={{
                      ...sx.backBtn,
                      ...(isMobile
                        ? { width: "100%", textAlign: "center" }
                        : {}),
                    }}
                    onClick={() => navigate(-1)}
                  >
                    ← Back
                  </button>

                  <button
                    style={{
                      ...sx.nextBtn,
                      ...(isMobile ? { width: "100%" } : {}),
                      opacity: selectedTime ? 1 : 0.7,
                      cursor: selectedTime ? "pointer" : "not-allowed",
                    }}
                    disabled={!selectedTime}
                    onClick={() => {
                      const isAny = staffId === "any";
                      const finalStaffId =
                        isAny ? assignedStaffId || null : staffId;
                      const finalStaffName =
                        isAny ? assignedStaffName || "Any staff" : staffName;

                      navigate("/confirm", {
                        state: {
                          staffId: finalStaffId || "any",
                          staffName: finalStaffName,
                          date: dateStr,
                          start: +selectedTime,
                          services,
                          totals: {
                            count: servicesCount,
                            totalPrice: totalCost,
                            totalMin: durationMin,
                          },
                        },
                      });
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            </div>
            {/* end bottom bar */}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Availability helpers ---------------- */

function dayBounds(ymd) {
    const [y, m, d] = ymd.split("-").map(Number);
    const start = new Date(y, m - 1, d, 0, 0, 0, 0);
    const end = new Date(y, m - 1, d + 1, 0, 0, 0, 0);
    return { start, end };
  }  

function normalizeTime(v) {
  if (!v) return null;

  if (typeof v?.toDate === "function") {
    const d = v.toDate();
    return isNaN(+d) ? null : d;
  }

  if (typeof v === "string" && v.startsWith("Timestamp(")) {
    const m = v.match(/seconds=(\d+),\s*nanoseconds=(\d+)/);
    if (m) {
      const seconds = Number(m[1]);
      const d = new Date(seconds * 1000);
      return isNaN(+d) ? null : d;
    }
  }

  if (v instanceof Date) return isNaN(+v) ? null : v;

  if (typeof v === "string" || typeof v === "number") {
    const d = new Date(v);
    return isNaN(+d) ? null : d;
  }

  return null;
}

function getWorkHoursForDate(day) {
    const dow = day.getDay();
  
    if (dow === 0) {
      // Sunday
      return { workStart: 11, workEnd: 18 };
    }
  
    // Monday–Saturday
    return { workStart: 10, workEnd: 20 };
  }
  

//
// UPDATED: supports fractional hour start (10.5 = 10:30)
//
function makeSlotsForDay(dayStart, fromHour, toHour, stepMin) {
  const slots = [];

  const start = new Date(dayStart);
  const startHour = Math.floor(fromHour);
  const startMin = Math.round((fromHour % 1) * 60);
  start.setHours(startHour, startMin, 0, 0);

  const end = new Date(dayStart);
  const endHour = Math.floor(toHour);
  const endMin = Math.round((toHour % 1) * 60);
  end.setHours(endHour, endMin, 0, 0);

  for (let t = +start; t < +end; t += stepMin * 60 * 1000) {
    slots.push(new Date(t));
  }

  return slots;
}

function canFitAndIsFree(
  slotStart,
  durationMin,
  staffBookings,
  workEndHour,
  bufferMin = 0
) {
  const day = new Date(slotStart);
  const hardClose = new Date(day);
  hardClose.setHours(workEndHour, 0, 0, 0);

  const slotStartBuf = new Date(+slotStart - bufferMin * 60 * 1000);
  const slotEnd = new Date(+slotStart + durationMin * 60 * 1000);
  const slotEndBuf = new Date(+slotEnd + bufferMin * 60 * 1000);

  if (+slotEnd > +hardClose) return false;

  const hasOverlap =
    staffBookings?.some((b) => {
      const bStart = normalizeTime(b.start);
      const bEnd = normalizeTime(b.end);
      if (!bStart || !bEnd) return false;

      return +slotStartBuf < +bEnd && +slotEndBuf > +bStart;
    }) ?? false;

  return !hasOverlap;
}

function firstFreeStaff(slotStart, durationMin, byStaff, workEndHour, bufferMin) {
  for (const sid of Object.keys(byStaff || {})) {
    const list = byStaff[sid] || [];
    if (canFitAndIsFree(slotStart, durationMin, list, workEndHour, bufferMin)) {
      return { id: sid, name: sid };
    }
  }
  return null;
}

function chunk(arr, n) {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

/* ---------------- formatters ---------------- */

function toYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fmtTime(d) {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

function formatMinutes(min) {
  if (!min || !isFinite(min)) return "0m";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/* ---------------- styles ---------------- */

const sx = {
  screen: {
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    fontFamily:
      "Work Sans, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  header: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "18px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  heroBoxDesktop: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "0 20px",
    position: "relative",
  },
  heroBoxMobile: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "0 16px",
    position: "relative",
  },

  heroImage: {
    width: "100%",
    height: 210,
    borderRadius: 4,
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  heroOverlay: {
    position: "absolute",
    left: 40,
    top: 64,
    display: "flex",
    gap: 12,
    alignItems: "center",
    background: "rgba(113,93,89,0.85)",
    padding: "10px 14px",
    borderRadius: 10,
    color: "#fff",
  },
  heroOverlayMobile: {
    left: 16,
    top: 40,
    right: 16,
  },

  heroTitle: { fontSize: 18, fontWeight: 700 },
  heroMeta: { fontSize: 13, fontWeight: 500, opacity: 0.95 },

  containerDesktop: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "18px 20px 40px",
  },
  containerMobile: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "18px 16px 32px",
  },

  centerRow: { display: "flex", justifyContent: "center" },
  calendarBox: { width: "100%", maxWidth: 768 },

  sectionTitle: {
    fontSize: 35,
    fontWeight: 700,
    color: "#101827",
    margin: "12px 0 10px",
  },

  dateInput: {
    width: "100%",
    maxWidth: 320,
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: 16,
    fontWeight: 600,
  },

  timeWrap: {
    width: 768,
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  timeWrapMobile: {
    width: "100%",
    borderRadius: 12,
  },

  timeHeader: {
    background: "#5c5470",
    color: "#fff",
    padding: "14px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  timeHeaderText: { fontSize: 17, fontWeight: 900 },

  timeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
    gap: 10,
    padding: 12,
  },

  timeBtn: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    background: "#ffffff",
    fontSize: 15,
    fontWeight: 700,
    color: "#030303",
    cursor: "pointer",
    textAlign: "center",
  },
  timeBtnActive: {
    background: "#f6f6fb",
    boxShadow: "inset 0 0 0 2px #5c5470",
  },

  bottomBarDesktop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    maxWidth: 768,
    width: "100%",
    paddingTop: 16,
  },
  bottomBarMobile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12,
    maxWidth: 480,
    width: "100%",
    paddingTop: 20,
  },

  summary: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#6b7280",
    fontSize: 13,
    fontWeight: 700,
  },
  dot: { width: 6, height: 6, borderRadius: 999, background: "#6b7280" },

  backBtn: {
    background: "#e7e9ee",
    color: "#334155",
    border: 0,
    borderRadius: 22,
    padding: "12px 18px",
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
  },
  nextBtn: {
    background: "#5c5470",
    color: "#fff",
    border: 0,
    borderRadius: 22,
    padding: "12px 20px",
    fontWeight: 700,
    fontSize: 16,
    cursor: "pointer",
  },
};
